import { actions } from "@mixitone/actions";
import tauri from "lib/tauri";
import type { SessionAdapter } from "lib/session/SessionAdapter";
import { decodeAccessToken, jwtHasDeviceToken } from "./mfaSessionJwt";

function sessionAdapter(): SessionAdapter {
  const adapter = window.sessionAdapter;
  if (!adapter) {
    throw new Error("Session adapter not found");
  }
  return adapter;
}

/**
 * Elevate the current session to aal2 after a successful TOTP code.
 * Tauri / device-token JWTs: edge elevateSession + applyElevatedAccessToken.
 * Web GoTrue: challengeAndVerifyTotp so RLS sees a real aal2 session.
 */
export async function elevateMfaSession(totpCode: string): Promise<void> {
  const adapter = sessionAdapter();
  const session = await adapter.getSession();
  const jwt = decodeAccessToken(session?.access_token);
  const useDeviceElevate = tauri.enabled || jwtHasDeviceToken(jwt);

  if (useDeviceElevate) {
    const result = await actions.mfa.elevateSession({ totpCode });
    if (!result.accessToken) {
      throw new Error("Could not elevate session");
    }
    const applied = await adapter.applyElevatedAccessToken(result.accessToken);
    if (applied.error) throw applied.error;
    return;
  }

  const factors = await adapter.listTotpFactors();
  if (factors.error) throw factors.error;
  const verified = (factors.data?.factors ?? []).find((factor) => factor.status === "verified");
  if (!verified) {
    throw new Error("No authenticator enrolled");
  }

  const challenged = await adapter.challengeAndVerifyTotp(verified.id, totpCode);
  if (challenged.error) throw challenged.error;
}
